import { Localized, useLocalization } from "@fluent/react"
import { LoadingButton } from "@mui/lab"
import { Box, Chip, Divider, IconButton, SxProps, TextField, Theme, Tooltip, Typography } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EditIcon from '@mui/icons-material/Edit'
import RemoveDoneIcon from '@mui/icons-material/RemoveDone'
import ReplayIcon from '@mui/icons-material/Replay'
import { createContext, MouseEvent, useContext, useMemo, useState } from "react"
import { AppId } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { formatTime, parseAndFormatISODate, parseISODate } from "../../app/Utils"
import {
    changeDocCommentStatus,
    createComment,
    deleteDocComment,
    DocComment,
    updateComment,
} from "../documents/commentsAPI"
import { showError } from "../notifications/notificationsSlice"
import { selectLoggedUser } from "../user/userSlice"
import { buildRaCommentIndex, commentsFor, RaAnchor, RaAnchorKind, RaCommentIndex } from "./raComments"
import { anchorKey, useComposingIn, useIsCommentSelected, useRaCompose, useRaSelection } from "./RaSelection"

/**
 * Everything a comment needs beyond its anchor: the document, and the guid of
 * the RISK_ANALYSIS section — which is what belongs in `sectionGuid`. See
 * raComments.ts for why that matters.
 *
 * One context rather than two, and separate from the view's own DocIdContext,
 * so this module never has to import from SimpleRiskAnalysis.tsx and create a
 * cycle (a context caught in one can evaluate to undefined).
 */
export interface RaCommentScope {
    documentId: AppId
    sectionGuid: string
}
export const RaCommentScopeContext = createContext<RaCommentScope>({ documentId: '', sectionGuid: '' })

/**
 * Comments grouped by anchor, built once per render of the form.
 *
 * Deliberately a context rather than a prop: the cells sit inside the memoised
 * FunctionCellBody, and a context update repaints them without the index having
 * to travel down through every level as a prop that would defeat the memo on
 * every unrelated change.
 */
export const RaCommentIndexContext = createContext<RaCommentIndex>(buildRaCommentIndex([]))

const anchorLabelId: Record<RaAnchorKind, string> = {
    'function': 'ra-comment-on-function',
    'defect': 'ra-comment-on-defect',
    'consequence': 'ra-comment-on-consequence',
    'reason': 'ra-comment-on-reason',
    'control-method': 'ra-comment-on-control-method',
    'recommended-action': 'ra-comment-on-recommended-action',
}

const anchorFallback: Record<RaAnchorKind, string> = {
    'function': 'Funkcja',
    'defect': 'Wada',
    'consequence': 'Skutek',
    'reason': 'Przyczyna',
    'control-method': 'Śr. kontroli',
    'recommended-action': 'Zal. działanie',
}

const chipSx = { height: '18px', fontSize: '.65rem', fontWeight: 800 }

/** What the comment is about. The one thing these threads carry that the document-level panel does not. */
const AnchorChip = ({ kind }: { kind: RaAnchorKind }) =>
    <Chip
        size='small'
        variant='outlined'
        sx={chipSx}
        label={<Localized id={anchorLabelId[kind]}>{anchorFallback[kind]}</Localized>} />

/** Mirrors CommentStatusLabel in TextWithComments; the codes differ, the look does not. */
const StatusChip = ({ status }: { status: DocComment['status'] }) => {
    const sx = { ...chipSx, ml: 'auto' }
    switch (status) {
        case 'NEW':
            return <Chip sx={sx} size='small' color='info' label={<Localized id='comment-status-new'>Nowy</Localized>} />
        case 'WONTFIX':
            return <Chip sx={sx} size='small' color='error' label={<Localized id='comment-status-rejected'>Odrzucony</Localized>} />
        default:
            return <Chip sx={sx} size='small' color='success' label={<Localized id='comment-status-resolved'>Rozwiązany</Localized>} />
    }
}

const borderGray = '1px solid var(--gray-200)'
const borderActive = '2px solid var(--blue-400)'

export interface RaCommentsCellProps {
    /** Anchors whose comments render on this row — and whose new comments are composed here. */
    anchors: RaAnchor[]
    canAdd: boolean
    canResolve: boolean
    /** The grid cell styling supplied by the caller (borders, gridRow, padding). */
    sx: SxProps<Theme>
}

/**
 * The FMEA table's comments column cell: one per reason row.
 *
 * Threads render inline rather than in a popper — the table is a grid, and the
 * `.ra-bottom-toolbar` positioning CommentsSection relies on is CSS-scoped to
 * `#simple-risk-analysis-grid` / `.ra-grid`, neither of which wraps this table.
 *
 * The cards themselves follow `CommentsPanel` in TextWithComments.tsx: a
 * bordered box per thread that turns blue when opened, collapsed to head +
 * content + reply count until then, with the actions and the reply box
 * appearing only on the open one. Same component at a smaller scale — the
 * column is a table track, so the padding and type are a step down.
 */
export const RaCommentsCell = (props: RaCommentsCellProps) => {
    const { anchors, canAdd, canResolve, sx } = props
    const index = useContext(RaCommentIndexContext)
    const compose = useRaCompose()

    // A comment is started from the add button on the tile it is about; it is
    // written here, in the cell that holds that tile's threads.
    const keys = useMemo(() => anchors.map(anchorKey), [anchors])
    const composingKey = useComposingIn(keys)
    const composing = canAdd && composingKey !== null
        ? anchors.find(a => anchorKey(a) === composingKey) ?? null
        : null

    const threads: { comment: DocComment, anchor: RaAnchor }[] = []
    for (const a of anchors) {
        for (const c of commentsFor(index, a)) {
            threads.push({ comment: c, anchor: a })
        }
    }

    // Array form rather than a spread: `SxProps` may legitimately be an array or
    // a callback, and spreading either of those silently yields an empty object —
    // the cell would lose its borders with nothing to show for it.
    //
    // Pinned to the last track of whatever grid it lands in, so the comments
    // column stays rightmost however many columns a view puts before it.
    return <Box sx={[
        { gridColumn: '-2' },
        ...(Array.isArray(sx) ? sx : [sx]),
        { display: 'flex', flexDirection: 'column', gap: .5, fontSize: '0.75rem' },
    ]}>
        {threads.map(t => <RaCommentThread
            key={t.comment.id}
            comment={t.comment}
            anchor={t.anchor}
            canAdd={canAdd}
            canResolve={canResolve} />)}

        {composing !== null && <Box data-ra-composer sx={{ border: borderActive, borderRadius: 2, p: 1 }}>
            <NewRaComment
                // A fresh composer per tile, so a draft never carries over.
                key={composingKey}
                anchor={composing}
                onDone={() => compose.set(null)} />
        </Box>}
    </Box>
}

interface NewRaCommentProps {
    anchor: RaAnchor
    parentCommentId?: string | null
    onDone: () => void
}

/**
 * The composer, for a new thread and for a reply alike.
 *
 * A reply inherits its parent's anchor rather than being unanchored, so the
 * whole thread stays attached to the same fragment — and `commentsFor` never
 * has to guess where a stray reply belongs.
 */
const NewRaComment = (props: NewRaCommentProps) => {
    const { anchor, parentCommentId = null, onDone } = props
    const dispatch = useAppDispatch()
    const { documentId, sectionGuid } = useContext(RaCommentScopeContext)
    const { l10n } = useLocalization()

    const isReply = parentCommentId !== null
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)

    const send = async () => {
        const content = text.trim()
        if (!content) return

        setSending(true)
        try {
            await dispatch(createComment({
                comment: content,
                context: '',
                documentId,
                sectionGuid,
                parentCommentId,
                sectionType: 'GROUP',
                type: 'INTERNAL',
                pos: null,
                clientId: null,
                contextType: anchor.contextType,
                contextGuid: anchor.contextGuid,
            })).unwrap()
            setText('')
            onDone()
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSending(false)
        }
    }

    // On a reply the box is a permanent fixture of the open thread, so Cancel
    // empties it rather than closing anything; on a new thread it closes.
    const handleCancel = () => {
        setText('')
        onDone()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Enter sends a reply, as in the document-level panel. A new thread is
        // multiline, so there Enter has to stay a newline.
        if (isReply && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
        }
    }

    const placeholder = isReply
        ? l10n.getString('ra-comment-reply-placeholder', null, 'Odpowiedź...')
        : l10n.getString('ra-comment-placeholder', null, 'Komentarz...')

    return <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
        {!isReply && <AnchorChip kind={anchor.kind} />}
        <TextField
            focused={!isReply}
            autoFocus={!isReply}
            size='small'
            multiline={!isReply}
            fullWidth
            disabled={sending}
            placeholder={placeholder}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{ style: { fontSize: '0.75rem' } }} />
        <Box sx={{ display: 'flex', gap: .5 }}>
            {isReply
                ? <LoadingButton loading={sending} startIcon={<ReplayIcon />} variant='outlined' size='small' onClick={send}>
                    <Localized id='reply'>Odpowiedz</Localized>
                </LoadingButton>
                : <LoadingButton loading={sending} variant='outlined' size='small' onClick={send}>
                    <Localized id='save'>Zapisz</Localized>
                </LoadingButton>}
            <LoadingButton loading={sending} variant='outlined' size='small' color='error' onClick={handleCancel}>
                <Localized id='cancel'>Anuluj</Localized>
            </LoadingButton>
        </Box>
    </Box>
}

interface RaCommentThreadProps {
    comment: DocComment
    anchor: RaAnchor
    canAdd: boolean
    canResolve: boolean
}

/**
 * One thread, read straight from the store.
 *
 * CommentsSection keeps a local copy of the comment list instead, which is why
 * its edits never reach the counters elsewhere in the document. documentSlice
 * already reduces create / update / delete / status-change, so there is nothing
 * to mirror here.
 */
const RaCommentThread = (props: RaCommentThreadProps) => {
    const { comment: data, anchor, canAdd, canResolve } = props
    const { id, comment, createdBy, documentId, replies } = data

    const dispatch = useAppDispatch()
    const myUserName = useAppSelector(selectLoggedUser)
    const selection = useRaSelection()
    const selected = useIsCommentSelected(id)

    const [editing, setEditing] = useState(false)
    const [editText, setEditText] = useState(comment)
    const [saving, setSaving] = useState(false)

    const mine = myUserName === createdBy
    const canEdit = canAdd && mine && replies.length === 0

    // Opening a comment also lights up the fragment it is about — the table's
    // answer to the underline TextWithComments draws in the text.
    const handleSelect = () => {
        if (!selected) selection.select({ commentId: id, anchorKey: anchorKey(anchor) })
    }
    const handleClose = (e: MouseEvent) => {
        e.stopPropagation()
        selection.select(null)
    }

    const handleSaveEdit = async () => {
        const text = editText.trim()
        if (!text) return

        setSaving(true)
        try {
            await dispatch(updateComment({ ...data, comment: text })).unwrap()
            setEditing(false)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    const handleCancelEdit = () => {
        setEditText(comment)
        setEditing(false)
    }

    const handleDelete = async () => {
        // Guarded here rather than with `disabled`: a disabled child makes
        // Tooltip warn, and the button is only busy for the length of one round
        // trip before the thread unmounts.
        if (saving) return

        setSaving(true)
        try {
            await dispatch(deleteDocComment(data)).unwrap()
            // Holding the id of a comment that no longer exists would leave the
            // fragment highlighted with nothing pointing at it.
            selection.select(null)
        } catch (error) {
            dispatch(showError('error'))
            setSaving(false)
        }
    }

    // Same thunk as a top-level delete; deleteDocComment.fulfilled prunes the
    // parent's `replies` array as well as the entity table.
    const handleDeleteReply = async (reply: DocComment) => {
        try {
            await dispatch(deleteDocComment(reply)).unwrap()
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const setStatus = async (next: 'RESOLVED' | 'WONTFIX') => {
        try {
            await dispatch(changeDocCommentStatus({ id, documentId, status: next })).unwrap()
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const replyCount = replies.length

    return <Box
        data-ra-comment={id}
        onClick={handleSelect}
        sx={{
            border: selected ? borderActive : borderGray,
            borderRadius: 2,
            p: 1,
            bgcolor: 'background.paper',
            cursor: selected ? 'default' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: .5,
        }}>
        {selected && <>
            <Box sx={{ display: 'flex' }}>
                {canResolve && <Tooltip title={<Localized id='mark-as-resolved'>Oznacz jako rozwiązany</Localized>}>
                    <IconButton size='small' onClick={() => setStatus('RESOLVED')}><DoneAllIcon fontSize='small' /></IconButton>
                </Tooltip>}
                {canResolve && <Tooltip title={<Localized id='mark-as-rejected'>Oznacz jako odrzucony</Localized>}>
                    <IconButton size='small' onClick={() => setStatus('WONTFIX')}><RemoveDoneIcon fontSize='small' /></IconButton>
                </Tooltip>}
                {!editing && canEdit && <Tooltip title={<Localized id='edit'>Edytuj</Localized>}>
                    <IconButton size='small' onClick={() => setEditing(true)}><EditIcon fontSize='small' /></IconButton>
                </Tooltip>}
                {!editing && canEdit && <Tooltip title={<Localized id='delete'>Usuń</Localized>}>
                    <IconButton size='small' color='error' onClick={handleDelete}><DeleteIcon fontSize='small' /></IconButton>
                </Tooltip>}
                <Tooltip title={<Localized id='close'>Zamknij</Localized>}>
                    <IconButton sx={{ ml: 'auto' }} size='small' onClick={handleClose}><CloseIcon fontSize='small' /></IconButton>
                </Tooltip>
            </Box>
            <Divider />
        </>}

        <CommentHead comment={data} anchor={anchor} />

        {editing
            ? <Box>
                <TextField
                    fullWidth
                    multiline
                    size='small'
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    inputProps={{ style: { fontSize: '0.75rem' } }} />
                <Box sx={{ display: 'flex', gap: .5, mt: .5 }}>
                    <LoadingButton loading={saving} size='small' variant='outlined' onClick={handleSaveEdit}>
                        <Localized id='save'>Zapisz</Localized>
                    </LoadingButton>
                    <LoadingButton loading={saving} size='small' variant='outlined' color='error' onClick={handleCancelEdit}>
                        <Localized id='cancel'>Anuluj</Localized>
                    </LoadingButton>
                </Box>
            </Box>
            : <Box sx={{ whiteSpace: 'pre-wrap' }}>{comment}</Box>}

        {replyCount === 0
            ? <Typography component='div' color='text.secondary' variant='caption'>
                <Localized id='ra-comment-replies' vars={{ count: replyCount }}>
                    <span>{`${replyCount} odpowiedzi`}</span>
                </Localized>
            </Typography>
            : <Box>
                <Chip
                    size='small'
                    icon={<ReplayIcon />}
                    label={<Localized id='ra-comment-replies' vars={{ count: replyCount }}>
                        <span>{`${replyCount} odpowiedzi`}</span>
                    </Localized>}
                    color='primary'
                    variant='outlined'
                    sx={{ ...chipSx, fontWeight: 400, px: .5 }} />
            </Box>}

        {selected && <Divider />}

        {selected && replyCount > 0 && <Box sx={{
            background: 'var(--slate-50)',
            display: 'flex',
            flexDirection: 'column',
            gap: .25,
            p: .5,
        }}>
            {replies.map(r => <Box key={r.id} sx={{
                border: '1px solid var(--slate-400)',
                background: 'var(--slate-200)',
                borderRadius: 2,
                p: .5,
                display: 'flex',
                flexDirection: 'column',
            }}>
                <CommentHead comment={r} />
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{r.comment}</Box>
                {r.createdBy === myUserName && canAdd && <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
                    <Tooltip title={<Localized id='delete'>Usuń</Localized>}>
                        <IconButton size='small' sx={{ p: .25 }} onClick={() => handleDeleteReply(r)}>
                            <DeleteIcon sx={{ width: '14px', height: '14px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>}
            </Box>)}
        </Box>}

        {selected && canAdd && <NewRaComment
            anchor={anchor}
            parentCommentId={id as string}
            onDone={() => { }} />}
    </Box>
}

/**
 * Author, date, time, status — the same line CommentHead draws in
 * TextWithComments, with the anchor chip in front on a top-level comment.
 */
const CommentHead = ({ comment, anchor }: { comment: DocComment, anchor?: RaAnchor }) => {
    const { createdByFullName, createDate, status } = comment
    return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: .5,
        flexWrap: 'wrap',
        [`& label`]: {
            fontWeight: 800,
        },
        [`& > span`]: {
            fontSize: '.65rem',
            color: 'text.secondary',
        },
    }}>
        {anchor && <AnchorChip kind={anchor.kind} />}
        <label>{createdByFullName}</label>
        <span>{parseAndFormatISODate(createDate, false)}</span>
        <span>{createDate ? formatTime(parseISODate(createDate)) : ''}</span>
        <StatusChip status={status} />
    </Box>
}
